/**
 * RWBR — EZRA PIT VIOLATIONS (EZRA-S2 §3.9, Appendix B1).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠  THERE ARE THREE VERSIONS OF THIS CHART AND THEY DISAGREE. D-016 PICKED
 *    THE WRONG ONE, BECAUSE THE RIGHT ONE WAS INVISIBLE AT THE TIME.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   1. `RWBR_pit_violations_MH.pdf` — Mike Haley's sheet. 36 rows, with UNDER
 *      CAUTION and UNDER GREEN columns. Already rejected by D-016.
 *
 *   2. The EZRA Track Pack — TWO separate 36-row charts, one Green Flag and one
 *      Yellow Flag, with penalties written in EZRA's own shorthand (Pass thru to
 *      ELLG, 2nd Pit Stop to ELDG) plus four notes and a two-code legend.
 *      **This is what D-016 chose.**
 *
 *   3. EZRA-S2 **Appendix B1** — one 36-row chart, penalties inline, every
 *      offence carrying a real NASCAR rulebook citation (9-15C, 10-4B, …).
 *      Headed `2d6c`. It was an embedded image in the docx, so it did not exist
 *      as far as any text search was concerned, and D-016 was decided without it.
 *
 * RULING (D-EZ-7): **Appendix B1 governs.**
 *
 * Not on grounds of taste — on grounds of what the rules text points at. EZRA-S2
 * §3.9 says, in full: *"Pit Violation Chart. For use when the RACE CAM DICE
 * result in a Pit Violation. See Appendix B1 for Results."* The governing
 * document names its own appendix. This is the same principle as D-EZ-1, which
 * already ruled that EZRA Season 2 governs over the 2016 Haley material where
 * the two conflict; the Track Pack is a parallel artifact, not the rule set.
 *
 * The three genuinely disagree, so this is not a cosmetic choice. Row 34 is a
 * TIRE VIOLATION in B1 and LUG NUTS NOT INSTALLED in the Track Pack, with
 * different penalties. Row 35 is lug nuts in B1 and equipment removal in the
 * Track Pack. B1 also carries offences the Track Pack has no row for at all
 * ("Car pitting out of the assigned pit box", which it prints twice, at 22 and
 * 46), and drops others.
 *
 * ── HOW IT IS ROLLED ───────────────────────────────────────────────────────
 *
 * The header reads `2d6c` and the index runs 11-66, not 2-12: this is the
 * project's `code1166` read (red die tens, white die units), the same as every
 * other 36-row chart in the game. `dice.code1166()` already does it.
 */

import type { Dice } from './dice.ts';

export const CITE_PIT_VIOLATIONS =
  'EZRA-S2 §3.9 / Appendix B1 — "PIT VIOLATION CHART". Embedded in EZRAS2RulesFinal.docx as an EMF ' +
  'image (word/media/image10.emf), which is why no text extraction of that file ever showed it.';

export const CITE_B1_GOVERNS =
  'D-EZ-7 — EZRA-S2 §3.9 says "See Appendix B1 for Results", so the Season 2 appendix governs over ' +
  'the EZRA Track Pack\'s two-chart version and over the Mike Haley sheet. Same principle as D-EZ-1.';

/**
 * What a violation actually costs. EZRA writes the penalty as prose and the
 * prose repeats, so it is read into a small closed set — otherwise every row
 * would need its own branch in the engine and the chart could not be checked.
 */
export type PitPenaltyKind =
  /** "Restart at the tail of the field." */
  | 'tail-of-field'
  /**
   * "Must pass through pits under green; Restart at the tail of the field if
   * under yellow." The commonest row by far, and the only one whose effect
   * depends on the flag.
   */
  | 'pass-through-or-tail'
  /** "PENALTY: 1 lap" — a genuine lap down, into the LDSG. */
  | 'lap-penalty'
  /** "PENALTY: Must come in" — a second stop. */
  | 'second-stop'
  /** "PENALTY: Stop and Go". */
  | 'stop-and-go'
  /** "PENALTY: NASCAR Discretion" — see the ruling below. */
  | 'discretion'
  /** Row 51 only: "(Roll for Feud)" and no positional penalty at all. */
  | 'feud-roll';

export interface PitViolationRow {
  /** 11-66. */
  code: string;
  /** The offence, verbatim, including its NASCAR rulebook citation. */
  text: string;
  penalty: PitPenaltyKind;
}

/**
 * Appendix B1, all 36 rows, transcribed.
 *
 * Duplicated offences are printed duplicates, not transcription errors: pitting
 * out of the assigned box appears at 22 and 46, lug nuts at 35 and 52, tire
 * violation at 34 and 55, speeding-entering at 14 and 64, speeding-exiting at 43
 * and 62, driving through more than 3 boxes at 16 and 53, running over/under
 * equipment at 41 and 54, removing equipment at 36 and 56.
 */
export const PIT_VIOLATIONS: readonly PitViolationRow[] = [
  { code: '11', text: 'Pitting before Pit Road is open (Section 10-4B in Rule Book) ... PENALTY: Restart at the tail of the field.', penalty: 'tail-of-field' },
  { code: '12', text: 'Pitting out of order (10-4B) ... PENALTY: Restart at the tail of the field', penalty: 'tail-of-field' },
  { code: '13', text: 'Car must enter pit road in single file (9-15C) ... PENALTY: Must pass through pits under green; Restart at the tail of the field if under yellow', penalty: 'pass-through-or-tail' },
  { code: '14', text: 'Speeding – Entering pits (9-15D) ... PENALTY: Must pass through pits under green; Restart at the tail of the field if under yellow', penalty: 'pass-through-or-tail' },
  { code: '15', text: 'All passing on pit road must be to the outside on entry (9-15C) ... PENALTY: Must pass through pits under green; Restart at the tail of the field if under yellow', penalty: 'pass-through-or-tail' },
  { code: '16', text: 'Driving thru more than 3 pit boxes (9-15C) ... PENALTY: Must pass through pits under green; Restart at the tail of the field if under yellow', penalty: 'pass-through-or-tail' },
  { code: '21', text: 'Crew member(s) over the wall too soon (9-15E) ... PENALTY: Must pass through pits under green; Restart at the tail of the field if under yellow', penalty: 'pass-through-or-tail' },
  { code: '22', text: 'Car pitting out of the assigned pit box (9-15F) ... PENALTY: 1 lap', penalty: 'lap-penalty' },
  { code: '23', text: 'Use of extension poles are limited/not self illuminated (9-15G) ... PENALTY: Must pass through pits under green; Restart at the tail of the field if under yellow', penalty: 'pass-through-or-tail' },
  { code: '24', text: 'Too many crew member(s) in contact with pit service area (9-15H) ... PENALTY: Must pass through pits under green; Restart at the tail of the field if under yellow', penalty: 'pass-through-or-tail' },
  { code: '25', text: 'Crew member(s) returning from equipment side of wall (9-15H) ... PENALTY: Must pass through pits under green; Restart at the tail of the field if under yellow', penalty: 'pass-through-or-tail' },
  { code: '26', text: 'Front air wrench must be carried back to pit wall side of car (9-15J) ... PENALTY: Must pass through pits under green; Restart at the tail of the field if under yellow', penalty: 'pass-through-or-tail' },
  { code: '31', text: 'Using more than two (2) air wrenches during one pit stop (9-15J) ... PENALTY: Must pass through pits under green; Restart at the tail of the field if under yellow', penalty: 'pass-through-or-tail' },
  { code: '32', text: 'Non-Compliant refueling .. PENALTY: Must pass through pits under green; Restart at the tail of the field if under yellow', penalty: 'pass-through-or-tail' },
  { code: '33', text: 'Tossing or throwing the fuel filler/equipment (9-15M) ... PENALTY: Must pass through pits under green; Restart at the tail of the field if under yellow', penalty: 'pass-through-or-tail' },
  { code: '34', text: 'Tire violation-Removing outside tire acceptable/Rolling a tire(s) beyond the center of pit road (9-15P) ... PENALTY: Must pass through pits under green; Restart at the tail of the field if under yellow', penalty: 'pass-through-or-tail' },
  { code: '35', text: 'Lug nut(s) not installed (9-15N) ... PENALTY: Must come in', penalty: 'second-stop' },
  { code: '36', text: 'Removing equipment from assigned pit area (9-15O) ... PENALTY: Stop and Go', penalty: 'stop-and-go' },
  { code: '41', text: 'Running over/under equipment (9-15O) ... PENALTY: NASCAR Discretion', penalty: 'discretion' },
  { code: '42', text: 'Hand pushing car more than 3 pit boxes (9-15Q) ... PENALTY: Must pass through pits under green; Restart at the tail of the field if under yellow', penalty: 'pass-through-or-tail' },
  { code: '43', text: 'Speeding – Exiting pits (9-15D) ... PENALTY: Must pass through pits under green; Restart at the tail of the field if under yellow', penalty: 'pass-through-or-tail' },
  { code: '44', text: 'Running the stop & go sign/light (10-4C) ... PENALTY: NASCAR Discretion', penalty: 'discretion' },
  { code: '45', text: 'Going above the blend line exiting the pits (9-11) ... PENALTY: Must pass through pits under green; Restart at the tail of the field if under yellow', penalty: 'pass-through-or-tail' },
  { code: '46', text: 'Car pitting out of the assigned pit box (9-15F) ... PENALTY: 1 lap', penalty: 'lap-penalty' },
  { code: '51', text: "Unsportsman like conduct directed to a compeditor's pit crew/team member. (Roll for Feud).", penalty: 'feud-roll' },
  { code: '52', text: 'Lug nut(s) not installed (9-15N) ... PENALTY: Must come in', penalty: 'second-stop' },
  { code: '53', text: 'Driving thru more than 3 pit boxes (9-15C) ... PENALTY: Must pass through pits under green; Restart at the tail of the field if under yellow', penalty: 'pass-through-or-tail' },
  { code: '54', text: 'Running over/under equipment (9-15O) ... PENALTY: NASCAR Discretion', penalty: 'discretion' },
  { code: '55', text: 'Tire violation-Removing outside tire acceptable/Rolling a tire(s) beyond the center of pit road (9-15P) ... PENALTY: Must pass through pits under green; Restart at the tail of the field if under yellow', penalty: 'pass-through-or-tail' },
  { code: '56', text: 'Removing equipment from assigned pit area (9-15O) ... PENALTY: Stop and Go', penalty: 'stop-and-go' },
  { code: '61', text: 'Disobeying NASCAR request (9-11) ... PENALTY: NASCAR Discretion', penalty: 'discretion' },
  { code: '62', text: 'Speeding – Exiting pits (9-15D) ... PENALTY: Must pass through pits under green; Restart at the tail of the field if under yellow', penalty: 'pass-through-or-tail' },
  { code: '63', text: 'Verbal abuse to a NASCAR official (9-11) ... PENALTY: NASCAR Discretion', penalty: 'discretion' },
  { code: '64', text: 'Speeding – Entering pits (9-15D) ... PENALTY: Must pass through pits under green; Restart at the tail of the field if under yellow', penalty: 'pass-through-or-tail' },
  { code: '65', text: 'Safety violation ... PENALTY: NASCAR Discretion', penalty: 'discretion' },
  { code: '66', text: 'Commitment Line Violation (9-15B) ... PENALTY: Must pass through pits under green; Restart at the tail of the field if under yellow', penalty: 'pass-through-or-tail' },
];

export const PIT_VIOLATIONS_BY_CODE: Readonly<Record<string, PitViolationRow>> = Object.freeze(
  Object.fromEntries(PIT_VIOLATIONS.map((r) => [r.code, r])),
);

/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  D-EZ-21 — "PENALTY: NASCAR Discretion", read off EZRA's OWN CHART
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * *Ruled by Howard, 16 Aug 2026. Supersedes the flat default this file used to
 * apply to all six rows.*
 *
 * Six of the thirty-six rows print `PENALTY: NASCAR Discretion` and nothing
 * else — 41, 44, 54, 61, 63, 65. It is not a penalty, it is a human at the
 * table deciding, and no table anywhere in EZRA turns it into one.
 *
 * ── THE EVIDENCE THAT WAS SITTING ON THE CHART ─────────────────────────────
 *
 * **B1 is internally consistent by NASCAR rulebook section.** Every offence
 * carries its section number, and every section that appears more than once
 * carries the SAME penalty on every row it appears on — with no exception
 * anywhere on the sheet:
 *
 *   9-15C  ×4  → pass-through-or-tail   (13, 15, 16, 53)
 *   9-15D  ×4  → pass-through-or-tail   (14, 43, 62, 64)
 *   9-15F  ×2  → lap-penalty            (22, 46)
 *   9-15H  ×2  → pass-through-or-tail   (24, 25)
 *   9-15J  ×2  → pass-through-or-tail   (26, 31)
 *   9-15N  ×2  → second-stop            (35, 52)
 *   9-15P  ×2  → pass-through-or-tail   (34, 55)
 *
 * That uniformity is asserted by test (`sectionsAreUniform`), because it is the
 * whole licence for what follows. If a future correction to the transcription
 * breaks it, the inference below stops being sound and the test says so
 * immediately rather than the ruling quietly rotting.
 *
 * ── SO FOUR OF THE SIX RESOLVE FROM PRINTED EVIDENCE ───────────────────────
 *
 * **9-15O appears FOUR times.** Two rows print `Stop and Go` (36, 56) and two
 * print `NASCAR Discretion` (41, 54). Given that every other repeated section
 * on this sheet is uniform, the natural reading is that 41 and 54 carry the
 * same penalty their section already carries: **Stop and Go.**
 *
 * **9-11 appears THREE times.** One row prints the pass-through (45) and two
 * print Discretion (61, 63). Same reading: **pass-through-or-tail** — which is
 * also what the old flat default already gave them, so those two rows do not
 * move. That agreement is worth noting rather than hiding: an inference that
 * reproduces the previous answer on the rows where both methods apply is an
 * inference with something behind it.
 *
 * ── AND TWO GENUINELY DO NOT ───────────────────────────────────────────────
 *
 * **44** cites `10-4C`, which appears nowhere else on the chart. **65** —
 * "Safety violation" — carries no section number at all. Neither has a sibling
 * to read from, so **both keep the mild default and both stay flagged.** They
 * are not quietly folded in with the four that were solved.
 *
 * The tempting inference on 44 is rejected on purpose: "Running the stop & go
 * sign/light" means the driver *ignored a penalty*, which in real NASCAR
 * escalates, and EZRA's harshest printed penalty is the lap. That is a
 * plausible story and it is not evidence, so it is not implemented.
 *
 * ── THE MEASURED EFFECT ────────────────────────────────────────────────────
 *
 * The share of served violations resolved by OUR guess rather than by EZRA
 * falls from **16.0% to 5.6%** — four of the six rows now come off the sheet.
 * `usedDiscretionDefault` stays true only for 44 and 65.
 */

/** The NASCAR rulebook section a row cites, or null when it prints none. */
export function sectionOf(row: PitViolationRow): string | null {
  const m = row.text.match(/\((?:Section\s+)?(\d+-\d+[A-Z]?)(?:\s+in Rule Book)?\)/);
  return m ? m[1] : null;
}

/**
 * Every section that appears on more than one row, with the penalty its
 * non-discretion rows agree on — or `null` when they disagree.
 *
 * A `null` here would mean the uniformity claim above is false, which is why
 * the test asserts there are none.
 */
export function sectionPenalties(): Map<string, PitPenaltyKind | null> {
  const bySection = new Map<string, Set<PitPenaltyKind>>();
  for (const row of PIT_VIOLATIONS) {
    if (row.penalty === 'discretion') continue;
    const section = sectionOf(row);
    if (!section) continue;
    const set = bySection.get(section) ?? new Set<PitPenaltyKind>();
    set.add(row.penalty);
    bySection.set(section, set);
  }
  const out = new Map<string, PitPenaltyKind | null>();
  for (const [section, kinds] of bySection) {
    out.set(section, kinds.size === 1 ? [...kinds][0] : null);
  }
  return out;
}

/** True when no repeated section carries two different penalties. D-EZ-21's licence. */
export function sectionsAreUniform(): boolean {
  return [...sectionPenalties().values()].every((v) => v !== null);
}

/**
 * What each Discretion row resolves to, and what licenses it.
 *
 * `resolved: null` means no sibling row exists and the mild default applies —
 * still our guess, still flagged.
 */
export interface DiscretionResolution {
  code: string;
  section: string | null;
  resolved: PitPenaltyKind | null;
  /** The rows on the same section that license the reading. */
  siblings: string[];
  why: string;
}

export function discretionResolution(code: string): DiscretionResolution {
  const row = PIT_VIOLATIONS_BY_CODE[code];
  if (!row) throw new RangeError(`no B1 row ${code}`);
  const section = sectionOf(row);
  if (!section) {
    return {
      code,
      section: null,
      resolved: null,
      siblings: [],
      why: 'The row cites no NASCAR section at all, so there is nothing on the chart to read it from.',
    };
  }
  const siblings = PIT_VIOLATIONS.filter(
    (r) => r.penalty !== 'discretion' && sectionOf(r) === section,
  );
  if (siblings.length === 0) {
    return {
      code,
      section,
      resolved: null,
      siblings: [],
      why: `Section ${section} appears nowhere else on the chart, so it has no sibling to read from.`,
    };
  }
  const kinds = new Set(siblings.map((r) => r.penalty));
  if (kinds.size !== 1) {
    return {
      code,
      section,
      resolved: null,
      siblings: siblings.map((r) => r.code),
      why: `Section ${section} carries more than one penalty elsewhere on the chart, so it settles nothing.`,
    };
  }
  const resolved = [...kinds][0];
  return {
    code,
    section,
    resolved,
    siblings: siblings.map((r) => r.code),
    why:
      `EZRA prints ${resolved} for section ${section} on row${siblings.length === 1 ? '' : 's'} ` +
      `${siblings.map((r) => r.code).join(', ')}, and every repeated section on this chart is uniform.`,
  };
}

/**
 * The mild default, kept for the rows D-EZ-21 could NOT resolve.
 *
 * Chosen because it is the penalty EZRA applies to twenty-two of the other
 * thirty rows, and because a rule you are unsure of should not be the harshest
 * one on the sheet.
 */
export const DISCRETION_DEFAULT: PitPenaltyKind = 'pass-through-or-tail';

/** Rows 44 and 65 — still unresolved after D-EZ-21, and still saying so. */
export function unresolvedDiscretionCodes(): string[] {
  return PIT_VIOLATIONS.filter((r) => r.penalty === 'discretion')
    .filter((r) => discretionResolution(r.code).resolved === null)
    .map((r) => r.code);
}

/** True while ANY discretion row is still running on our default rather than EZRA's chart. */
export const DISCRETION_IS_UNRESOLVED = true;

export const CITE_DISCRETION =
  'D-EZ-21 — EZRA-S2 Appendix B1 prints "PENALTY: NASCAR Discretion" on rows 41, 44, 54, 61, 63 and ' +
  '65 and defines it nowhere. Every NASCAR rulebook section that appears more than once on the chart ' +
  'carries the same penalty on every row, so four of the six are read off their own section: 9-15O ' +
  '(rows 36, 56) resolves 41 and 54 to Stop and Go, and 9-11 (row 45) resolves 61 and 63 to the ' +
  'pass-through. Rows 44 (10-4C, unique) and 65 (no section printed) have no sibling and keep the ' +
  'chart\'s commonest penalty as a flagged default.';

/** How many rows carry each penalty — used by the tests to pin the transcription. */
export function penaltyCounts(): Record<PitPenaltyKind, number> {
  const counts = {
    'tail-of-field': 0,
    'pass-through-or-tail': 0,
    'lap-penalty': 0,
    'second-stop': 0,
    'stop-and-go': 0,
    discretion: 0,
    'feud-roll': 0,
  } as Record<PitPenaltyKind, number>;
  for (const row of PIT_VIOLATIONS) counts[row.penalty]++;
  return counts;
}

export interface PitViolationResult {
  code: string;
  row: PitViolationRow;
  /** The penalty actually applied, after D-EZ-21 resolves any discretion. */
  penalty: PitPenaltyKind;
  /**
   * True ONLY when the row was Discretion AND D-EZ-21 could not read it off the
   * chart — rows 44 and 65. A row resolved from its own NASCAR section is not
   * our default and must not be counted as one.
   */
  usedDiscretionDefault: boolean;
  /** How a Discretion row was settled, when the roll landed on one. */
  discretion: DiscretionResolution | null;
  underYellow: boolean;
  /** Plain English, for the ledger. */
  description: string;
  ruleCitation: string;
  dice: { red: number; white: number; code: string };
}

/**
 * Roll a pit violation.
 *
 * `underYellow` decides the only row type whose effect is conditional — EZRA
 * writes twenty-two of the thirty-six as "pass through under green; tail of the
 * field under yellow", which is the same offence costing you far more when the
 * field is bunched up.
 */
export function rollPitViolation(dice: Dice, underYellow: boolean, driverName?: string): PitViolationResult {
  const roll = dice.code1166();
  const row = PIT_VIOLATIONS_BY_CODE[roll.code];
  if (!row) throw new RangeError(`pit violations: no Appendix B1 row for code ${roll.code}`);

  // D-EZ-21 — a Discretion row is read off its own NASCAR section where the
  // chart has one to read from, and falls back to the mild default only where
  // it does not (44 and 65).
  const discretion = row.penalty === 'discretion' ? discretionResolution(roll.code) : null;
  const usedDiscretionDefault = discretion !== null && discretion.resolved === null;
  const penalty = discretion ? (discretion.resolved ?? DISCRETION_DEFAULT) : row.penalty;
  const who = driverName ?? 'The car';

  return {
    code: roll.code,
    row,
    penalty,
    usedDiscretionDefault,
    discretion,
    underYellow,
    ruleCitation: discretion ? `${CITE_PIT_VIOLATIONS}; ${CITE_DISCRETION}` : CITE_PIT_VIOLATIONS,
    dice: { red: roll.red, white: roll.white, code: roll.code },
    description: `${roll.code} on the PIT VIOLATION CHART: ${row.text} ${describePenalty(penalty, underYellow, who)}`,
  };
}

export function describePenalty(penalty: PitPenaltyKind, underYellow: boolean, who: string): string {
  switch (penalty) {
    case 'tail-of-field':
      return `${who} restarts at the tail of the field.`;
    case 'pass-through-or-tail':
      return underYellow
        ? `The flag is out, so ${who} restarts at the tail of the field.`
        : `${who} has to pass through the pits under green.`;
    case 'lap-penalty':
      return `${who} is held a full lap — down a lap, into the lap-down group.`;
    case 'second-stop':
      return `${who} has to come back in for a second stop.`;
    case 'stop-and-go':
      return `${who} serves a stop and go.`;
    case 'feud-roll':
      return `No positional penalty — but there are words in the pits, and a feud is rolled for.`;
    case 'discretion':
      return `Left to the officials.`;
  }
}
